// src/components/enduser/subscription/FreeTrialFlow.tsx
// ניסיון חינם בעזרת קוד אימות - חלקים 3-4 (מאי 2026)
// מסך מקדים (עריכת טלפון בינלאומי + שליחה) → מסך הזנת קוד 6 ספרות → הפעלה.

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useEnduserAuth } from "../../../contexts/EnduserAuthContext";
import { enduserAuthService } from "../../../services/enduser/enduserAuthService";
import { subscriptionService } from "../../../services/enduser/subscriptionService";
import MotionCard, { MotionButton } from "../../shared/MotionCard";

type FreeTrialStep = "intro" | "code";

const isIsraeli = (phone: string): boolean =>
  enduserAuthService
    .formatInternationalPhoneNumber(phone || "")
    .startsWith("+972");

const FreeTrialFlow: React.FC = () => {
  const navigate = useNavigate();
  const { user, subscriptionStatus, verifySession } = useEnduserAuth();

  const [step, setStep] = useState<FreeTrialStep>("intro");

  // מסך מקדים - טלפון בפורמט בינלאומי, מאותחל מהפרופיל
  const [phone, setPhone] = useState<string>(
    enduserAuthService.formatInternationalPhoneNumber(user?.phone_number || "")
  );

  // מסך קוד
  const [code, setCode] = useState<string>("");
  const [deliveryMethod, setDeliveryMethod] = useState<"sms" | "email">("sms");
  const [deliveryTarget, setDeliveryTarget] = useState<string>("");

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [info, setInfo] = useState<string>("");

  const countryInfo = enduserAuthService.getPhoneCountryInfo(phone);
  const phoneIsValid = enduserAuthService.isValidInternationalPhone(phone);
  const willSendBySms = isIsraeli(phone);

  // ===== שליחת קוד =====
  const handleSendCode = async () => {
    setError("");
    setInfo("");

    if (!phoneIsValid) {
      setError(
        "מספר טלפון לא תקין. יש להזין בפורמט בינלאומי: +[קידומת מדינה][מספר]"
      );
      return;
    }

    setIsLoading(true);
    try {
      // הטלפון נשלח רק אם נערך ביחס לפרופיל; אחרת ה-Backend משתמש בטלפון מהפרופיל.
      const normalized = enduserAuthService.formatInternationalPhoneNumber(phone);
      const profilePhone = enduserAuthService.formatInternationalPhoneNumber(
        user?.phone_number || ""
      );
      const payload =
        normalized !== profilePhone ? { phone_number: normalized } : {};

      const result = await subscriptionService.sendFreeTrialCode(payload);

      if (result.res === "OK") {
        const method = result.data?.delivery_method || (willSendBySms ? "sms" : "email");
        setDeliveryMethod(method);
        setDeliveryTarget(
          method === "sms"
            ? result.data?.phone_number || normalized
            : result.data?.email || user?.email || ""
        );
        setCode("");
        setStep("code");
        setInfo(result.msg);
      } else {
        setError(result.msg || "שגיאה בשליחת הקוד");
      }
    } catch (e) {
      setError("שגיאה בשליחת הקוד. נסה שוב.");
    } finally {
      setIsLoading(false);
    }
  };

  // ===== אימות קוד =====
  const handleVerifyCode = async () => {
    setError("");
    setInfo("");

    if (code.length !== 6) {
      setError("יש להזין קוד בן 6 ספרות");
      return;
    }

    setIsLoading(true);
    try {
      const result = await subscriptionService.verifyFreeTrialCode(code);

      if (result.res === "OK") {
        // רענון סטטוס המנוי כדי ש-can_calculate / free_trial_remaining יתעדכנו ב-context
        await verifySession();
        navigate("/enduser/dashboard", {
          state: { freeTrialActivated: true },
        });
      } else {
        // טיפול בקודי שגיאה מה-Backend
        switch (result.error_code) {
          case "CODE_EXPIRED":
          case "NO_CODE_SENT":
          case "TOO_MANY_ATTEMPTS":
            // צריך קוד חדש - מנקים את הקוד ומשאירים במסך הקוד עם אפשרות שליחה מחדש
            setCode("");
            setError(result.msg);
            break;
          case "INVALID_CODE":
            // קוד שגוי - ה-Backend כבר כולל את יתרת הניסיונות בהודעה
            setCode("");
            setError(result.msg);
            break;
          case "FREE_TRIAL_EXHAUSTED":
            // נגמרו הניסיונות החינמיים - מפנים ל-paywall
            setError(result.msg);
            setTimeout(() => navigate("/enduser/subscription"), 2500);
            break;
          default:
            setError(result.msg || "שגיאה באימות הקוד");
        }
      }
    } catch (e) {
      setError("שגיאה באימות הקוד. נסה שוב.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setCode("");
    setError("");
    await handleSendCode();
  };

  // ===== מסך מקדים (Step 1) =====
  const renderIntroStep = () => (
    <MotionCard className="p-8" hoverEffect="none">
      <div className="text-center mb-6">
        <div className="text-5xl mb-3" aria-hidden="true">
          🎟️
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          ניסיון חינם - {subscriptionStatus?.free_trial_max ?? 2} חישובים
        </h2>
        <p className="text-gray-600">
          כדי להפעיל את הניסיון החינם, נשלח לך קוד אימות בן 6 ספרות.
        </p>
      </div>

      <div className="mb-4">
        <label
          htmlFor="free-trial-phone"
          className="block text-sm font-medium text-gray-700 mb-2"
        >
          מספר טלפון (פורמט בינלאומי) <span className="text-red-500">*</span>
        </label>
        <input
          id="free-trial-phone"
          type="tel"
          dir="ltr"
          value={phone}
          onChange={(e) => {
            setPhone(e.target.value);
            setError("");
          }}
          aria-required="true"
          aria-describedby="free-trial-phone-help"
          placeholder="+972541234567"
          disabled={isLoading}
          className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-left tracking-wide"
        />
        <p
          id="free-trial-phone-help"
          className="text-xs text-gray-500 mt-1"
        >
          יש להזין בפורמט בינלאומי, לדוגמה: ‎+972541234567‎ (ישראל) או ‎+15551234567‎.
        </p>

        {/* זיהוי מדינה + ערוץ שליחה */}
        {phone && phoneIsValid && (
          <div className="mt-3 flex items-center justify-between rounded-md bg-blue-50 px-3 py-2 text-sm">
            <span className="text-blue-800">
              {countryInfo.name && countryInfo.name !== "לא זוהה"
                ? `מדינה: ${countryInfo.name}`
                : "מספר בינלאומי"}
            </span>
            <span className="font-medium text-blue-700">
              {willSendBySms ? "📱 יישלח ב-SMS" : "✉️ יישלח למייל"}
            </span>
          </div>
        )}
      </div>

      {error && (
        <div
          role="alert"
          className="mb-4 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700"
        >
          {error}
        </div>
      )}

      <MotionButton
        variant="primary"
        size="lg"
        className="w-full"
        onClick={handleSendCode}
        disabled={isLoading || !phoneIsValid}
        loading={isLoading}
      >
        {isLoading ? "שולח קוד..." : "שלח קוד אימות"}
      </MotionButton>

      <button
        type="button"
        onClick={() => navigate("/enduser/dashboard")}
        className="mt-4 w-full text-sm text-gray-500 hover:text-gray-700 underline"
      >
        אולי מאוחר יותר
      </button>
    </MotionCard>
  );

  // ===== מסך הזנת קוד (Step 2) =====
  const renderCodeStep = () => (
    <MotionCard className="p-8" hoverEffect="none">
      <div className="text-center mb-6">
        <div className="text-5xl mb-3" aria-hidden="true">
          {deliveryMethod === "sms" ? "📱" : "✉️"}
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">הזן קוד אימות</h2>
        <p className="text-gray-600">
          {deliveryMethod === "sms"
            ? "נשלח קוד בן 6 ספרות ב-SMS למספר:"
            : "נשלח קוד בן 6 ספרות לכתובת המייל:"}
        </p>
        {deliveryTarget && (
          <p className="font-medium text-blue-600 mt-1" dir="ltr">
            {deliveryTarget}
          </p>
        )}
      </div>

      <div className="mb-4">
        <label
          htmlFor="free-trial-code"
          className="block text-sm font-medium text-gray-700 mb-2"
        >
          קוד אימות <span className="text-red-500">*</span>
        </label>
        <input
          id="free-trial-code"
          type="text"
          inputMode="numeric"
          dir="ltr"
          value={code}
          onChange={(e) => {
            setCode(e.target.value.replace(/\D/g, "").slice(0, 6));
            setError("");
          }}
          aria-required="true"
          placeholder="123456"
          maxLength={6}
          disabled={isLoading}
          className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-center text-lg tracking-widest"
        />
        <p className="text-xs text-gray-500 mt-1 text-center">
          הזן 6 ספרות · הקוד תקף ל-10 דקות
        </p>
      </div>

      {error && (
        <div
          role="alert"
          className="mb-4 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700"
        >
          {error}
        </div>
      )}
      {info && !error && (
        <div className="mb-4 rounded-md bg-green-50 border border-green-200 px-3 py-2 text-sm text-green-700">
          {info}
        </div>
      )}

      <MotionButton
        variant="primary"
        size="lg"
        className="w-full"
        onClick={handleVerifyCode}
        disabled={isLoading || code.length !== 6}
        loading={isLoading}
      >
        {isLoading ? "מאמת..." : "אמת והפעל ניסיון"}
      </MotionButton>

      <div className="mt-4 flex items-center justify-between text-sm">
        <button
          type="button"
          onClick={() => {
            setStep("intro");
            setCode("");
            setError("");
            setInfo("");
          }}
          className="text-gray-600 hover:text-gray-800 underline"
        >
          חזרה לעריכת הטלפון
        </button>
        <button
          type="button"
          onClick={handleResend}
          disabled={isLoading}
          className="text-blue-600 hover:text-blue-800 underline disabled:opacity-50"
        >
          שלח קוד חדש
        </button>
      </div>
    </MotionCard>
  );

  return (
    <div
      className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4"
      dir="rtl"
    >
      <div className="max-w-md w-full">
        {step === "intro" ? renderIntroStep() : renderCodeStep()}
      </div>
    </div>
  );
};

export default FreeTrialFlow;
